import * as swcHelpers from '@swc/helpers';
import { takeLatest, put, all, call, select } from 'redux-saga/effects';
import { DataLayer } from '@grandvisionhq/analytics';
import { Actions } from '@grandvisionhq/configurator';
import { actions } from '@grandvisionhq/state';
import { startPrescriptionGlassesConfiguration } from '../actions.js';
import { updatePrescriptionValue, updatePrescriptionType, selectLensTypeSuccess, selectPackageSuccess, selectAddOnSuccess, resetSelectedAddOn } from '../configurations/actions.js';
import { fileUploadResult } from './actions.js';
import { getProductConfig, getConfigFlow, getUser } from './selectors.internal.js';
import { hasVisitedStepPrescription, getSelectedFrameProductType } from '../configurator/selectors.js';

function isSun(productType) {
    var ref;
    if (typeof productType === 'string') {
        return productType === 'Sunglasses';
    }
    return ((ref = productType.obj) === null || ref === void 0 ? void 0 : ref.name) === 'Sunglasses';
}
const getEventType = (p)=>{
    const productType = typeof p === 'string' ? p : p.productType;
    return isSun(productType) ? 'RxSunConfigEvent' : 'RxGlassesConfigEvent';
};
function* onStartConfiguration({ payload: { frame  }  }) {
    const openConfiguratorEvent = {
        event: getEventType(frame),
        eventAction: 'click on button',
        eventLabel: 'configure glasses button'
    };
    yield put(DataLayer.Actions.pushEvent(openConfiguratorEvent));
}
function* onPushEvent(id, eventAction, eventLabel) {
    const productConfig = yield select(getProductConfig, {
        id
    });
    const configFlow = yield select(getConfigFlow, {
        id
    });
    const user = yield select(getUser);
    const hasVisitedStepPrescription$1 = yield select(hasVisitedStepPrescription, {
        id
    });
    const productType = yield select(getSelectedFrameProductType);
    if (productType !== 'Frames' && productType !== 'Sunglasses') {
        console.error(`Did not receive a valid product type for pushing an event to the data layer. Expected "Frames" or "Sunglasses", received: ${productType}`);
        return;
    }
    const DATA_KEY = isSun(productType) ? 'RxSun' : 'RxGlasses';
    const action = DataLayer.Actions.pushEvent(swcHelpers.objectSpread({
        event: getEventType(productType),
        eventAction,
        eventLabel
    }, hasVisitedStepPrescription$1 && user ? {
        user
    } : {}, {
        [DATA_KEY]: {
            configFlow,
            productConfig
        }
    }));
    yield put(action);
}
/**
 * Actions that respond to different product selections during configuration of a product.
 */ const createSelectionHandler = (id, eventAction)=>function*() {
        yield call(onPushEvent, id, eventAction);
    }
;
const manualLensTypeSelection = (a)=>actions.isType(a, selectLensTypeSuccess) && a.payload.reason === 'user selection'
;
const manualLensPackageSelection = (a)=>actions.isType(a, selectPackageSuccess) && a.payload.reason === 'user selection'
;
const manualAddOnSelection = (a)=>(actions.isType(a, selectAddOnSuccess) || actions.isType(a, resetSelectedAddOn)) && a.payload.reason === 'user selection'
;
/**
 * Logs a 'view step' event an every step change
 */ function* onViewStep({ payload: { id  }  }) {
    yield call(onPushEvent, id, 'view step');
}
/**
 * Logs a 'select rx attribute' on every prescription value change
 * Also logs an event label with the parameter name and eye side, i.e. "axis left"
 */ function* onUpdatePrescriptionValue(id, { payload: { parameter , side  }  }) {
    yield call(onPushEvent, id, 'select rx attribute', `${parameter} ${side}`);
}
/**
 * Logs an 'existing customer' value change
 */ function* onUpdatePrescriptionType(id, { payload: { prescriptionType , reason  }  }) {
    if (reason === 'default selection') {
        return;
    }
    /**
   * Values discussed with Helena Heno on 2021/06/09. As of today, this list is not yet
   * represented in any documentation.
   */ const rxInputMethodMapping = {
        NewPrescription: {
            eventAction: 'manual prescription',
            eventLabel: 'manual'
        },
        EmailPrescription: {
            eventAction: 'email prescription',
            eventLabel: 'email'
        },
        ExistingCustomer: {
            eventAction: 'existing customer',
            eventLabel: 'customer'
        },
        PrescriptionFile: {
            eventAction: 'upload prescription',
            eventLabel: 'upload'
        },
        'book-appointment': {
            eventAction: 'book-appointment',
            eventLabel: 'book-appointment'
        }
    };
    const event = rxInputMethodMapping[prescriptionType];
    if (!event) {
        return;
    }
    yield call(onPushEvent, id, event.eventAction, event.eventLabel);
}
function* onFileUploadResult(id, { result  }) {
    if (result) {
        yield call(onPushEvent, id, 'upload message', result);
    }
}
/**
 * Logs when successfully moved to the next step
 */ function* onNextStep({ payload: { id  }  }) {
    yield call(onPushEvent, id, 'click on button', 'next step');
}
/**
 * This saga binds the the configurator ID to the selection actions, which is necessary to provide
 * the correct data for data layer events.
 */ const getConfigurationChangesSagas = (id)=>[
        takeLatest(updatePrescriptionValue.type, (a)=>onUpdatePrescriptionValue(id, a)
        ),
        takeLatest(updatePrescriptionType.type, (a)=>onUpdatePrescriptionType(id, a)
        ),
        // upload
        takeLatest(fileUploadResult.type, (a)=>onFileUploadResult(id, a)
        ),
        takeLatest(manualLensTypeSelection, createSelectionHandler(id, 'select lens type')),
        takeLatest(manualLensPackageSelection, createSelectionHandler(id, 'select lens package')),
        takeLatest(manualAddOnSelection, createSelectionHandler(id, 'select add-on')),
        /**
   * Configurator stepping actions
   */ takeLatest(Actions.isStepShownWithId(id), onViewStep),
        takeLatest(Actions.isNextStepWithId(id), onNextStep), 
    ]
;
function* listenForConfigurationChanges(id) {
    yield all(getConfigurationChangesSagas(id));
}
var dataLayerSagas = [
    takeLatest(startPrescriptionGlassesConfiguration.type, onStartConfiguration), 
];

export { dataLayerSagas as default, getConfigurationChangesSagas, listenForConfigurationChanges, onFileUploadResult, onPushEvent, onUpdatePrescriptionType };
//# sourceMappingURL=sagas.js.map
